/**
 * A call transcript as a conversation: consecutive lines from the same person
 * fold under one name, the way a chat log reads, but every phrase keeps its
 * own row and its own clock. A phrase is one recorded chunk, cut at the pause
 * after it, so the blank space between rows IS the pause the speaker made.
 * Used both for the live panel on the channel page (where new lines land at
 * the bottom while you watch) and for the finished transcript on the call
 * page, so the two never drift apart in look.
 */
import { useEffect, useRef } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import type { VoiceTranscriptSegment } from '../../lib/voiceCallsApi';
import { Avatar } from '../tasks/linear/Avatar';

interface SpeakerRun {
  key: string;
  /** Who: the participant row (Meet), else the user (voice), else the name. */
  speakerKey: string;
  userId: string | null;
  participantId: string | null;
  speakerName: string;
  startedAt: string;
  lines: VoiceTranscriptSegment[];
}

/** Still counts as "at the live edge" within this many pixels of the bottom. */
const FOLLOW_THRESHOLD_PX = 120;

/**
 * The scrollable box the transcript actually lives in (the panel with
 * `overflow-y: auto`), or null when it sits in normal page flow.
 *
 * Deliberately NOT `scrollIntoView`: that scrolls every scrollable ancestor,
 * the page included, so each arriving phrase dragged everyone watching a
 * screen share down to the transcript. Moving the panel's own `scrollTop`
 * keeps the scrolling inside the panel, where it belongs.
 */
function scrollParentOf(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/** Fold consecutive lines by the same speaker into one run. */
export function groupBySpeaker(segments: VoiceTranscriptSegment[]): SpeakerRun[] {
  const runs: SpeakerRun[] = [];
  for (const segment of segments) {
    const last = runs[runs.length - 1];
    const speakerKey = segment.participantId ?? segment.userId ?? `name:${segment.speakerName}`;
    if (last && last.speakerKey === speakerKey) {
      last.lines.push(segment);
    } else {
      runs.push({
        key: segment.id,
        speakerKey,
        userId: segment.userId,
        participantId: segment.participantId ?? null,
        speakerName: segment.speakerName,
        startedAt: segment.startedAt,
        lines: [segment],
      });
    }
  }
  return runs;
}

const timeFormat = new Intl.DateTimeFormat('ro-RO', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/**
 * Plain text of a transcript for the clipboard: one "[HH:MM:SS] Name: text"
 * line per phrase, a blank line between phrases. Pasted into notes or a task
 * it reads at a glance and every line can be quoted with its time.
 */
export function transcriptToText(segments: VoiceTranscriptSegment[]): string {
  return segments
    .map(
      (line) =>
        `[${timeFormat.format(new Date(line.startedAt))}] ${line.speakerName}: ${line.text}`,
    )
    .join('\n\n');
}

export function VoiceTranscript({
  segments,
  meId,
  meParticipantId,
  followLatest = false,
  emptyText,
}: {
  segments: VoiceTranscriptSegment[];
  meId?: string;
  /** Meet: our own participant row, so our lines read "Tu" even as a guest. */
  meParticipantId?: string;
  /** Keep the newest line in view as it arrives (live panel). */
  followLatest?: boolean;
  emptyText: string;
}) {
  const { t } = useI18n();
  const rootRef = useRef<HTMLOListElement | null>(null);
  const runs = groupBySpeaker(segments);

  useEffect(() => {
    if (!followLatest) return;
    const root = rootRef.current;
    if (!root) return;
    const scroller = scrollParentOf(root);
    if (!scroller) return;
    // Only when the reader is already at the live edge. Someone who scrolled up
    // to re-read an earlier phrase keeps their place instead of being yanked
    // back down every time a new line lands.
    const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    if (distanceFromBottom > FOLLOW_THRESHOLD_PX) return;
    scroller.scrollTop = scroller.scrollHeight;
  }, [followLatest, segments.length]);

  if (runs.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyText}</p>;
  }

  return (
    <ol ref={rootRef} className="space-y-5">
      {runs.map((run) => {
        const isMe =
          (Boolean(meParticipantId) && run.participantId === meParticipantId) ||
          (Boolean(meId) && run.userId === meId);
        return (
          // Speaker header on its own row; the spoken text then starts at the
          // very left and uses the whole panel width, with the clock tucked
          // under each phrase instead of eating a left gutter.
          <li key={run.key}>
            <div className="flex items-center gap-2">
              <Avatar name={run.speakerName} size={22} />
              <span className="min-w-0 truncate text-sm font-semibold text-foreground">
                {isMe ? t('teamChat.voiceYou') : run.speakerName}
              </span>
            </div>
            <ol className="mt-1.5 space-y-3">
              {run.lines.map((line) => (
                <li key={line.id}>
                  <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/90">
                    {line.text}
                  </p>
                  <time
                    dateTime={line.startedAt}
                    className="mt-0.5 block text-[11px] tabular-nums text-muted-foreground"
                  >
                    {timeFormat.format(new Date(line.startedAt))}
                  </time>
                </li>
              ))}
            </ol>
          </li>
        );
      })}
    </ol>
  );
}
